import { ResponsiveBar } from '@nivo/bar'
import type { BarDatum, BarLayer } from '@nivo/bar'

import {
  AUTONOMY_COLORS,
  AUTONOMY_DEFINITIONS,
  AUTONOMY_KEYS,
  AUTONOMY_LABELS,
  chartTheme,
  formatMonth,
} from './chartTheme'
import { TotalLabelLayer } from './TotalLabelLayer'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AutonomySplitCounts {
  autonomous: number
  assisted: number
  manual: number
  total: number
}

export interface AutonomySplitBucket extends AutonomySplitCounts {
  period: string
}

export interface AutonomySplit {
  totals: AutonomySplitCounts
  buckets: AutonomySplitBucket[]
}

const share = (value: number, total: number): number =>
  total > 0 ? Math.round((value / total) * 100) : 0

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

interface TooltipProps {
  id: string | number
  indexValue: string | number
  data: BarDatum
}

const BarTooltip = ({ id, indexValue, data }: TooltipProps) => {
  const total = (data.total as number) ?? 0
  return (
    <div className="rounded-lg bg-white px-3 py-2 shadow-md ring-1 ring-gray-200">
      <p className="mb-1.5 text-xs font-medium text-gray-500">
        {formatMonth(String(indexValue))}
      </p>
      <div className="flex flex-col gap-1">
        {AUTONOMY_KEYS.map((key) => {
          const count = (data[key] as number) ?? 0
          return (
            <div
              key={key}
              className={`flex items-center justify-between gap-4 ${
                String(id) === key ? 'font-semibold' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ backgroundColor: AUTONOMY_COLORS[key] }}
                />
                <span className="text-xs text-gray-700">
                  {AUTONOMY_LABELS[key]}
                </span>
              </div>
              <span className="text-xs text-gray-700">
                {count} ({share(count, total)}%)
              </span>
            </div>
          )
        })}
      </div>
      <p className="mt-1.5 flex justify-between border-t border-gray-100 pt-1.5 text-xs font-semibold text-gray-700">
        <span>Answered</span>
        <span>{total}</span>
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface VoEAutonomySplitChartProps {
  data: AutonomySplit | null | undefined
}

export const VoEAutonomySplitChart = ({ data }: VoEAutonomySplitChartProps) => {
  if (!data || data.totals.total === 0) {
    return (
      <div className="flex h-[350px] items-center justify-center text-sm text-gray-400">
        No answered verifications in this period
      </div>
    )
  }

  const { totals, buckets } = data

  return (
    <div className="flex flex-col gap-4">
      {/* Share of the whole range */}
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {AUTONOMY_KEYS.map((key) => (
          <div key={key} className="flex items-baseline gap-2">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: AUTONOMY_COLORS[key] }}
            />
            <span className="text-xs font-medium text-gray-600">
              {AUTONOMY_LABELS[key]}
            </span>
            <span className="text-sm font-semibold text-gray-800">
              {share(totals[key], totals.total)}%
            </span>
            <span className="text-xs text-gray-400">({totals[key]})</span>
          </div>
        ))}
      </div>

      <div className="h-[350px]">
        <ResponsiveBar
          data={buckets as unknown as BarDatum[]}
          keys={[...AUTONOMY_KEYS]}
          indexBy="period"
          groupMode="stacked"
          margin={{ top: 30, right: 20, bottom: 40, left: 50 }}
          padding={0.35}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={(bar) => AUTONOMY_COLORS[String(bar.id)] ?? '#94a3b8'}
          enableLabel={false}
          enableGridY={false}
          axisBottom={{ tickSize: 0, tickPadding: 8, format: formatMonth }}
          axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 5 }}
          theme={chartTheme}
          tooltip={BarTooltip}
          layers={[
            'grid',
            'axes',
            'bars',
            TotalLabelLayer as BarLayer<BarDatum>,
            'markers',
            'annotations',
          ]}
        />
      </div>

      {/* Definitions */}
      <dl className="grid grid-cols-1 gap-4 border-t border-gray-100 pt-3 md:grid-cols-3">
        {AUTONOMY_KEYS.map((key) => (
          <div key={key}>
            <dt className="text-xs font-semibold text-gray-700">
              {AUTONOMY_LABELS[key]}
            </dt>
            <dd className="mt-0.5 text-[11px] leading-snug text-gray-500">
              {AUTONOMY_DEFINITIONS[key]}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}
